import React from 'react';
import ReactDOM from 'react-dom';
import draculaTheme from 'prism-react-renderer/themes/dracula';

import {
    Appear,
    Box,
    CodePane,
    Deck,
    FlexBox,
    Grid,
    Heading,
    Image,
    ListItem,
    Markdown,
    Notes,
    OrderedList,
    Slide,
    Stepper,
    Text,
    UnorderedList,
    indentNormalizer,
    Link
} from 'spectacle';

import theme from './theme';
import template from './template';

import headshot from '../assets/blue_hair_headshot.jpg';
import twitterLogo from '../assets/Twitter_Logo_Blue.svg';
import TAndPLogo from '../assets/t&p-favicon.png';
import livingWithDisabilities from '../assets/living-with-disabilities_legend.jpg';
import hearing from  '../assets/living-with-disabilities_hearing.jpg';
import ambulatory from '../assets/living-with-disabilities_ambulatory.jpg';
import vision from '../assets/living-with-disabilities_vision.jpg';
import sefCare from '../assets/living-with-disabilities_selfcare.jpg';
import cognitive from '../assets/living-with-disabilities_cognitive.jpg';
import independent from '../assets/living-with-disabilities_independent.jpg';
import brokenArm from '../assets/broken_arm.jpg';
import catKeyboard from '../assets/mouse_cat.jpg';
import sickAtComputer from '../assets/sick_at_computer.jpg';
import computerVision from '../assets/computer_vision.jpg';
import screenGlare from '../assets/mobile_screen_glare.jpg';
import loudRoomVideo from '../assets/video_in_loud_room.png';
import eslintLogo from '../assets/eslint_logo.png';
import eslintTerminal from '../assets/eslint_terminal.png';
import eslintIDE from '../assets/IDE_screenshot.png';
import jsxA11yRulesDoc from '../assets/jsx-a11y_rules_documentation.png';
import jsxA11yRuleDetail from '../assets/jsx-a11y_rule_detail.png';
import dequeLogo from '../assets/deque_logo.png';
import reactAxeScreenshot from '../assets/react-axe_screenshot.png';
import reactAxeScreenshotExpanded from '../assets/react-axe_screenshot_expanded.png';
import axeDocumentation1 from '../assets/aXe_documentation_1.png';
import axeDocumentation2 from '../assets/aXe_documentation_2.png';
import axeDocumentation3 from '../assets/aXe_documentation_3.png';
import ghostBusterBooks from '../assets/ghost_busters_book_stack.jpg';
import storybookLogo from '../assets/storybook_logo.png';
import storybookExampleButton from '../assets/example-button-args.png';
import storybookVideo from '../assets/toolbar-walkthrough-optimized.mp4';
import storybookAddon from '../assets/storybook_a11y_addon.png';
import jestLogo from '../assets/jest_logo.png';
import jestAxeOutput from '../assets/jest-axe_output.png';
import canTelephone from '../assets/can_telephone.jpg';
import seleniumLogo from '../assets/selenium_logo_large.png';
import webdriverChromeWindow from '../assets/webdriver_chrome_window.png';
import webdriverResults1 from '../assets/webdriver_results_page1.png';
import webdriverResults2 from '../assets/webdriver_results_page2.png';
import webdriverResults3 from '../assets/webdriver_results_page3.png';
import webdriverResults4 from '../assets/webdriver_results_page4.png';
import cypressLogo from '../assets/cypress-logo.png';
import cypressAppScreenshot from '../assets/cypress_app.png';
import cypressAppRunTests from '../assets/cypress_app_run_test.png';
import cypressAppRunResults from '../assets/cypress_run_results.png';
import cypressRunResultsError from '../assets/cypress_run_results_error.png';
import cypressRunResultsInConsole from '../assets/cypress_results_in_console.png';
import outOfTheBox from '../assets/out_of_the_box.jpg';
import githubLogo from '../assets/github_logo.png';
import githubActionResults from '../assets/GitHub_Action_Results.png';
import githubActionLintResults from '../assets/GitHub_Action_Results_lint.png';
import githubActionTestResults from '../assets/gitHub_Action_Results_unittest.png';
import jenkinsLogo from '../assets/Jenkins-logo.png';
import netlifyLogo from '../assets/netlify_logo.png';
import travisLogo from '../assets/travisci_logo.png';
import circleciLogo from '../assets/circle-logo-horizontal-white.png';
import LighthouseLogo from '../assets/lighthouse-logo.png';
import pa11yLogo from '../assets/pa11y_logo.png';
import lighthouseAppInstall from '../assets/Lighthouse_GitHub_App_Install.png';
import lighthouseAppStatusCheck from '../assets/Lighthouse_Status_Check.png';
import lighthouseReport from '../assets/Lighthouse_Report.png';
import jenkinsPlugin from '../assets/Jenkins_Lighthouse_Plugin.png';
import pa11yTravisResults from '../assets/pa11y_travis_results.png';


const Presentation = () => (
    <Deck theme={theme} template={template} transitionEffect="fade">
        {/* Title Page */}
        <Slide>
            <FlexBox
                height="100%"
                flexDirection="column"
            >
                <Heading color="primary" fontSize="h1">
                    Auditing React Apps for Accessibility
                </Heading>
                <Heading fontSize="h2">Tips and Tricks</Heading>
                <FlexBox
                    paddingTop="130px"
                    width="100%"
                    justifyContent="space-evenly"
                >
                    <Box>
                        <Text color="secondary" fontWeight="bold" fontSize="3em">by Erin Doyle</Text>
                    </Box>
                    <Box>
                        <Text fontFamily="monospace" fontSize="2em">React Summit 2020</Text>
                    </Box>
                </FlexBox>
            </FlexBox>
        </Slide>
        {/* Who am I? */}
        <Slide>
            <Heading marginBottom="20px">Who am I?</Heading>
            <Grid gridTemplateColumns="1fr 2fr" gridColumnGap={15}>
                <Box backgroundColor="primary" border="10px inset #FF0080" overflowY="hidden">
                    <Image src={headshot} width="100%" />
                </Box>
                <Box backgroundColor="secondary" overflowY="hidden">
                    <OrderedList>
                        <Appear elementNum={0}>
                            <ListItem paddingBottom={5} paddingTop={4}>
                                <Text>Fullstack Senior Developer at:</Text>
                                <FlexBox justifyContent="flex-start" alightItems="center">
                                    <Box padding="0 8px 0 15px" marginTop="10px">
                                        <Image src={TAndPLogo} />
                                    </Box>
                                    <Link href="theoryandprinciple.com">Theory and Principle</Link>
                                    <Text fontStyle="italic" fontSize="0.8em">
                                        &nbsp;&nbsp;(A Legal and Justice Technology design firm)
                                    </Text>
                                </FlexBox>
                            </ListItem>
                        </Appear>
                        <Appear elementNum={1}>
                            <ListItem paddingBottom={5}>
                                <Text>Egghead.io Instructor</Text>
                                <Link href="https://egghead.io/courses/develop-accessible-web-apps-with-react?af=f27y1u">https://egghead.io/courses/develop-accessible-web-apps-with-react</Link>
                            </ListItem>
                        </Appear>
                        <Appear elementNum={2}>
                            <ListItem paddingBottom={5}>
                                <Text>Open Source Software contributor</Text>
                                <Link href="https://github.com/erin-doyle">https://github.com/erin-doyle</Link>
                            </ListItem>
                        </Appear>
                        <Appear elementNum={3}>
                            <ListItem>
                                <FlexBox justifyContent="flex-start" alightItems="center">
                                    <Text>Erin Doyle (</Text>
                                    <Box marginTop="10px" marginRight="-10px">
                                        <Image src={twitterLogo} height="70px" />
                                    </Box>
                                    <Link href="https://twitter.com/SunshinyDoyle">@SunshinyDoyle)</Link>
                                </FlexBox>
                            </ListItem>
                        </Appear>
                    </OrderedList>
                </Box>
            </Grid>
        </Slide>
        {/* Why is Accessibility Important? */}
        <Slide>
            <Box width="75%" margin="auto">
                <Heading>Why is Accessibility Important?</Heading>
            </Box>
        </Slide>
        {/* Inclusion - Population with Disability */}
        <Slide>
            <Heading>Inclusion</Heading>
            <FlexBox
                backgroundColor="#E5F3F4"
                flexDirection="column"
                justifyItems="center"
                flexBasis="1"
                marginLeft="30px"
                marginRight="30px"
            >
                <FlexBox
                    justifyContent="center"
                    alignItems="flex-end"
                >
                    <Box>
                        <Image src={livingWithDisabilities} alt="Living With Disabilities.  Number of Americans With a Disability by Age, Sex, and Disability Type" />
                    </Box>
                    <Box
                        paddingBottom="3px"
                        paddingLeft="10px"
                    >
                        <Link
                            href="https://www.census.gov/library/visualizations/2020/comm/living-with-disabilities.html?cid=living-with-disabilities" target="_blank"
                            fontSize="0.9em"
                        >
                            [Source: U.S. Census Bureau]
                        </Link>
                    </Box>
                </FlexBox>
                <Grid
                    gridTemplateColumns="1fr 1fr"
                    gridTemplateRows="1fr 1fr 1fr"
                    gridColumnGap={20}
                >
                    <FlexBox>
                        <Image src={hearing} alt="Number of Americans with Hearing difficulty"/>
                    </FlexBox>
                    <FlexBox>
                        <Image src={ambulatory} alt="Number of Americans with Ambulatory difficulty"/>
                    </FlexBox>
                    <FlexBox>
                        <Image src={vision} alt="Number of Americans with Vision difficulty"/>
                    </FlexBox>
                    <FlexBox>
                        <Image src={sefCare} alt="Number of Americans with Self-Care difficulty"/>
                    </FlexBox>
                    <FlexBox>
                        <Image src={cognitive} alt="Number of Americans with Cognitive difficulty"/>
                    </FlexBox>
                    <FlexBox>
                        <Image src={independent} alt="Number of Americans with Independent Living difficulty"/>
                    </FlexBox>
                </Grid>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - This infographic comes from the 2018 US Census.
                        - So this is only a small slice of our global population,
                        - but it gives us an example that there are millions of people using our web sites that have a disability or difficulty of some sort
                        - Many of the difficulties listed here make it especially hard for these people to use the web
                        - And additionally many people may not have a disability to begin with but as they age they develop these difficulties
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Inclusion - Temporary Limitations - 1 */}
        <Slide>
            <Heading fontSize="h3" textAlign="center">Inclusion - Temporary Limitations</Heading>
            <FlexBox
                padding="20px 40px 0"
                height="75%"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Box><Image src={brokenArm} height="550px" /></Box>
                <Box><Image src={catKeyboard} height="550px" /></Box>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - Injuries, broken arm / hand / wrist, recent surgery
                        - Cat in the way
                            - preventing people from being able to use a mouse
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Inclusion - Temporary Limitations - 2 */}
        <Slide>
            <Heading fontSize="h3" textAlign="center">Inclusion - Temporary Limitations</Heading>
            <FlexBox
                padding="20px 40px 0"
                height="75%"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Box><Image src={sickAtComputer} height="350px" /></Box>
                <Box><Image src={computerVision} height="350px" /></Box>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - Sick or on a medication that causes brain fogginess
                            - can cause cognitive or vision difficulties
                            - or can make it difficult to make fine motor movements using a mouse
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Inclusion - Situational or Environmental Limitations */}
        <Slide>
            <Heading fontSize="h3">Inclusion - Situational or Environmental Limitations</Heading>
            <FlexBox
                padding="20px 20px 0"
                height="70%"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box><Image src={screenGlare} height="350px" /></Box>
                <Box><Image src={loudRoomVideo} height="350px" /></Box>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - Using a mobile device outside where screen glare makes it hard to see
                        - Trying to watch a video in an area that's too loud to hear it
                            - or where it needs to be quiet and you can't turn the volume on
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* It's the law */}
        <Slide
            backgroundColor="tertiary"
            backgroundImage="url(https://static.politico.com/dims4/default/6230068/2147483647/resize/1920x/quality/90/?url=https%3A%2F%2Fstatic.politico.com%2Fb1%2Fd6%2Fb7daf6d24f128a1f5c132c1a23ef%2F20200210-doj-sign-ap-773.jpg)"
            backgroundOpacity={0.6}
        >
            <Heading color="secondary">It's the Law</Heading>
            <FlexBox
                margin="10px"
                flexDirection="row"
                justifyContent="space-evenly"
                alignItems="flex-start"
            >
                <Appear elementNum={0}>
                    <Box
                        backgroundColor="tertiary"
                        padding="10px"
                    >
                        <Box paddingBottom="5px">
                            <Heading fontSize="2em">Americans with Disabilities Act (ADA)</Heading>
                        </Box>
                        <UnorderedList>
                            <ListItem>
                                <Text>Regulated and enforced by the U.S. Department of Justice</Text>
                            </ListItem>
                            <ListItem>
                                <Text>Prohibits discrimination against individuals with disabilities</Text>
                            </ListItem>
                            <ListItem>
                                <Text>Applies to:</Text>
                                <UnorderedList>
                                    <ListItem>
                                        <Text>All areas of public life (jobs, schools, transportation)</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>All public and private places that are open to the general public</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>All electronic and information technology</Text>
                                    </ListItem>
                                </UnorderedList>
                            </ListItem>
                            <ListItem>
                                <Text>How to comply:</Text>
                                <UnorderedList>
                                    <ListItem>
                                        <Text>Meet the <Link href="https://www.w3.org/TR/WCAG20">WCAG (Web Content Accessibility Guidelines) 2.0</Link> Level AA</Text>
                                    </ListItem>
                                </UnorderedList>
                            </ListItem>
                        </UnorderedList>
                    </Box>
                </Appear>
                <Appear elementNum={1}>
                    <Box
                        backgroundColor="tertiary"
                        padding="10px"
                    >
                        <Box paddingBottom="10px">
                            <Heading fontSize="2em">Section 508 of the Rehabilitation Act of 1973</Heading>
                        </Box>
                        <UnorderedList>
                            <ListItem>
                                <Text>Prohibits discrimination on the basis of disability with regards to Federal Agencies</Text>
                            </ListItem>
                            <ListItem>
                                <Text>
                                    Section 508 adds the inclusion of electronic and information technology
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text>Applies to:</Text>
                                <UnorderedList>
                                    <ListItem>
                                        <Text>Programs conducted by Federal Agencies</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>Programs receiving federal financial assistance</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>Both Federal employment and Federal Contractor employment</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>All information and communications technology</Text>
                                    </ListItem>
                                </UnorderedList>
                            </ListItem>
                            <ListItem>
                                <Text>How to comply:</Text>
                                <UnorderedList>
                                    <ListItem>
                                        <Text fontSize="0.8em">Meet the&nbsp;
                                            <Link fontSize="0.8em" href="https://www.access-board.gov/guidelines-and-standards/communications-and-it/about-the-ict-refresh/final-rule/text-of-the-standards-and-guidelines">
                                                U.S. Access Board's ICT (Information and Communication Technology) Final Standards and Guidelines
                                            </Link>
                                        </Text>
                                    </ListItem>
                                </UnorderedList>
                            </ListItem>
                        </UnorderedList>
                    </Box>
                </Appear>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - In the United States, we have 2 major laws that require all web sites be accessible to those with disabilities.
                        - There are many similar laws in other countries around the world.
                        - As a result of these laws, there are thousands of lawsuits in the US alone each year against company's whose websites have been found to be inaccessible
                        - Both of these laws encourage using WCAG (or Web Content Accessibility Guidelines) version 2.0 level AA as a guide on how to meet the regulations
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* How do we get there? */}
        <Slide>
            <Box width="75%" margin="auto">
                <Heading>How do we get there?</Heading>
            </Box>
        </Slide>
        {/* Auditing tools */}
        <Slide>
            <Heading fontSize="h2">Auditing Tools and Automation</Heading>
            <Box margin="auto">
                <iframe src="https://giphy.com/embed/1nR6fu93A17vWZbO9c" width="680" height="520" frameBorder="0" class="giphy-embed" allowFullScreen></iframe>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - You don't have to be an expert in accessibility in order to get started in making your apps more accessible.
                            - There are a number of things that you can add your projects and workflow today to start improving the accessibility of your apps immediately.
                            - These tools will do a lot of the heavy lifting for you making it very easy to identify issues before they go to production.
                        - Additionally, taking a test-driven approach to accessibility:
                            - can really help you incrementally learn more about potential issues and how to resolve them
                            - most of these tools provide information and educational resources along with any findings that can help you learn more about the issue,
                            what groups are impacted, how severely they're impacted and how you can fix the issue
                        - Keep in mind that automated tools can only catch a percentage of _potential_ issues
                            - Manual testing using the assistive technology devices and strategies used by our users is still crucial in identifying
                            all accessibility issues or usability problems in your app
                            - But using tools and automating what we can will give us a big boost
                        - If we all used these tools and fixed just what they identified alone the web would be massively more accessible to our users
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - install */}
        <Slide>
            <Heading fontSize="h2">Linting</Heading>
            <FlexBox justifyContent="center">
                <Box><Image src={eslintLogo} width="300px" /></Box>
                <Box><Heading fontSize="3em" color="primary">eslint-plugin-jsx-a11y</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Install</Heading>
            <Box marginLeft="20px" marginRight="20px">
                <CodePane
                    theme={draculaTheme}
                    language="shell"
                    autoFillHeight
                >
                    {`
                    # npm
                    npm install eslint eslint-plugin-jsx-a11y --save-dev

                    # yarn
                    yarn add eslint eslint-plugin-jsx-a11y --dev
                    `}
                </CodePane>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - A plugin for ESLint that will run a static analysis check for any potential accessibility issues in your code
                        - If you're already running ESLint on your code (and you should be) this is a really easy way to start checking your
                        code for a11y issues
                        - install
                            - you'll need to install eslint if you haven't already
                            - and then install the plugin as development dependency
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - Enable and configure */}
        <Slide>
            <Heading fontSize="h2">Linting</Heading>
            <FlexBox justifyContent="center">
                <Box><Image src={eslintLogo} width="300px" /></Box>
                <Box><Heading fontSize="3em" color="primary">eslint-plugin-jsx-a11y</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Enable and Configure</Heading>
            <Stepper
                defaultValue={[]}
                values={[
                    [1, 4],
                    [8, 10],
                    [5, 7]
                ]}
            >
                {(value, step) => (
                    <Box position="relative" marginLeft="20px" marginRight="20px">
                        <CodePane
                            theme={draculaTheme}
                            language="json"
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            fontSize={18}
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                {
                                    "plugins": [
                                        "jsx-a11y"
                                    ],
                                    "extends": [
                                        "plugin:jsx-a11y/recommended"
                                    ],
                                    "rules": {
                                        "jsx-a11y/rule-name": 2
                                    }
                                }
                            `)}
                        </CodePane>

                        <Box
                            position="absolute"
                            bottom="0rem"
                            left="0rem"
                            right="0rem"
                            bg="black"
                        >
                            {step === 0 && (
                                <Text fontSize="1.5rem" margin="0rem">
                                    Enable
                                </Text>
                            )}

                            {step === 1 && (
                                <Text fontSize="1.5rem" margin="0rem">
                                    Set Specific Rules
                                </Text>
                            )}

                            {step === 2 && (
                                <Text fontSize="1.5rem" margin="0rem">
                                    Use the recommended rule set
                                </Text>
                            )}
                        </Box>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - you'll configure this plugin in your .eslintrc, .eslintrc.json file or in an eslintConfig field in package.json
                        - wherever you currently have eslint configured
                        - Enable
                            - (STEP)
                                - You enable the plugin by adding 'jsx-a11y' to the plugins array
                        - Configure
                            - (STEP)
                                - You can set any specific rules from the supported rules set in the rules object
                            - (STEP)
                                - You can also enable all the recommended rules at once by adding to the extends array
                                - If you want to use the strict rules set instead you'd just replace 'recommended' with 'strict'
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - Running CLI */}
        <Slide>
            <Heading fontSize="h2">Linting</Heading>
            <FlexBox justifyContent="center">
                <Box><Image src={eslintLogo} width="300px" /></Box>
                <Box><Heading fontSize="3em" color="primary">eslint-plugin-jsx-a11y</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Run from the command line</Heading>
            <FlexBox
                margin="10px"
                flexDirection="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Appear elementNum={0}>
                    <Box width="450px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.5rem" margin="0rem">
                                package.json
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={5}
                            highlightEnd={5}
                            fontSize={15}
                            language="json"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                {
                                    "scripts": {
                                        "start": "react-scripts start",
                                        "build": "react-scripts build",
                                        "lint": "eslint src",
                                        "pretest": "npm run lint",
                                        "test": "react-scripts test",
                                        "eject": "react-scripts eject"
                                    }
                                }
                            `)}
                        </CodePane>
                    </Box>
                </Appear>
                <Appear elementNum={1}>
                    <Box>
                        <Image src={eslintTerminal} width="800px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }} />
                    </Box>
                </Appear>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                        - To run eslint from the command line
                        - (STEP)
                            - you can just add a script to your package.json file
                        - (STEP)
                            - Then from your terminal run that command
                        - Let's look at the results:
                            - It lists all files in which potential accessibility issues were detected
                            - Under each file it lists the specific errors in that file
                            - And next to each error is the name of the rule that was violated
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - IDE Integration */}
        <Slide>
            <Heading fontSize="h2">Linting</Heading>
            <FlexBox justifyContent="center">
                <Box><Image src={eslintLogo} width="300px" /></Box>
                <Box><Heading fontSize="3em" color="primary">eslint-plugin-jsx-a11y</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">IDE Integration</Heading>
            <Box marginLeft="auto" marginRight="auto">
                <Image src={eslintIDE} style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }} />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Another really cool benefit of using linting to check for accessibility issues is that:
                            - Most IDEs today have integrations with eslint and can automatically show you any findings
                            right inline with your code
                            - This allows you to catch any potential errors immediately as you're writing the code.
                        -    Hovering over the errant code will show you what the finding is.
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - Github repo rules */}
        <Slide>
            <Box margin="20px auto">
                <Image src={jsxA11yRulesDoc} height="650px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }} />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - So if you then take one of the errors and look up the rule that was violated you can get more information about it
                        - The Github repo for the plugin has documentation of all of the rules it supports
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Linting - rule detail */}
        <Slide>
            <Box margin="20px auto">
                <Image src={jsxA11yRuleDetail} height="650px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }} />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - So in the example I just showed you, I had files that were violating the label-has-associated-control rule
                        - if we look up that rule we can see that:
                            - For each rule it details
                                1. what the pass and fail criteria is so you can understand how the rule works
                                2. suggestions for how to resolve the issue
                                3. and links to the WCAG guidelines that are specifically violated by this issue
                        - from there you can learn more about how this specific issue impacts the accessibility for users
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Runtime - Install and Enable */}
        <Slide>
            <Heading fontSize="h2">Runtime</Heading>
            <FlexBox justifyContent="center" paddingBottom="60px">
                <Box><Image src={dequeLogo} height="70px" /></Box>
                <Box><Heading fontSize="3em" color="primary">react-axe</Heading></Box>
            </FlexBox>
            <Stepper values={[0, [1, 9], [6, 9]]}>
                {(value, step) => (
                    <>
                        {step === 0 && (
                            <>
                                <Heading fontSize="2em">Install</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                        # npm
                                        npm install react-axe --save-dev

                                        # yarn
                                        yarn add react-axe --dev
                                        `}
                                    </CodePane>
                                </Box>
                            </>
                        )}
                        {(step === 1 || step === 2) && (
                            <>
                                <Heading fontSize="2em">Enable</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="javascript"
                                        highlightStart={value[0]}
                                        highlightEnd={value[1]}
                                        fontSize={18}
                                        autoFillHeight
                                    >
                                        {indentNormalizer(`
                                            import React from 'react';
                                            import ReactDOM from 'react-dom';

                                            import App from './App';

                                            if (process.env.NODE_ENV !== 'production') {
                                                const axe = require('react-axe');
                                                axe(React, ReactDOM, 1000);
                                            }

                                            ReactDOM.render(<App/>, document.getElementById('app-root'));
                                        `)}
                                    </CodePane>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - react-axe is an analysis tool for auditing your app for accessibility issues at runtime
                        - Any findings are output to the Chrome DevTools console
                        - react-axe layers nicely with the eslint-plugin-jsx-a11y in that
                            - the eslint plugin can only audit the static code
                            - but react-axe looks at the rendered DOM
                            - it can analyze the resulting DOM constantly as components are mounted, unmounted and state changes
                            - it can test for things like color contrast by being able to analyze the current foreground and
                            background colors during all states of an element
                        - (STEP)
                            - you install it as a development dependency just like the eslint plugin
                        - (STEP)
                            - To enable the tool
                        - (STEP)
                            - you'll need to initialize it early on in the code that starts up your app, before the call to render the root of your app
                            - You'll want to conditionally require react-axe so that it's only running in non Production environments as there is a bit of a performance impact when it runs
                            - To initialize it, you'll just pass the imported axe function the React and ReactDOM objects
                            - and the third argument is the timing delay in milliseconds that react-axe will wait to start analysis after detecting a component change
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Runtime - Running */}
        <Slide>
            <Heading fontSize="h2">Runtime</Heading>
            <FlexBox justifyContent="center" paddingBottom="15px">
                <Box><Image src={dequeLogo} height="70px" /></Box>
                <Box><Heading fontSize="3em" color="primary">react-axe</Heading></Box>
            </FlexBox>
            <Stepper values={[0]}>
                {(value, step) => (
                    <Box marginLeft="auto" marginRight="auto">
                        {step === -1 && (
                            <Image src={reactAxeScreenshot} width="1300px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }} />
                        )}
                        {step === 0 && (
                            <Image src={reactAxeScreenshotExpanded} width="1300px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }} />
                        )}
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Once installed and enabled react-axe will just run as your app runs, and output any findings to the Chrome DevTools console.
                        - This is the same page/component that I showed the eslint plugin had reported findings on
                            - but you can see here that react-axe is able to report even more findings of that same code now that it has the context
                            of the entire DOM and it's current state
                        - Looking at what react-axe outputs:
                            - it lists all found issues in order of severity of the impact to accessibility the problem represents
                            - if you expand one of the findings
                            - (STEP)
                                - It shows the errant source code
                                - If you hover over the Element listing here it will highlight the element on the page
                                - And it will list more details about the problem
                        - Finally, if you click on the link listed with the finding
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Runtime - aXe Documentation */}
        <Slide>
            <Stepper values={[0, 1]}>
                {(value, step) => (
                    <Box marginLeft="auto" marginRight="auto" marginTop="5px">
                        {step === -1 && (
                            <Image src={axeDocumentation1} width="1300px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }} />
                        )}
                        {step === 0 && (
                            <Image src={axeDocumentation2} width="1300px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }} />
                        )}
                        {step === 1 && (
                            <Image src={axeDocumentation3} width="1300px" style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }} />
                        )}
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Just like in the eslint plugin repo, here you can get more information about the issue
                        - Listed here are examples of how to resolve the issue and make the element in question accessible
                        - (STEP)
                            - There is a section showing:
                                - how severely accessibility is impacted for users
                                - Which specific disabilities are impacted
                                - And which WCAG and Section 508 guidelines are violated
                        - (STEP)
                            - And there is then further detail explaining in what ways accessibility is impacted by this issue
                            - which is really helpful in further understanding exactly how our code can impacts people's ability to use our applications
                            - And finally:
                                - a list of resources to learn even more through Deque University if desired
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library */}
        <Slide>
            <Heading fontSize="h2">Component Library</Heading>
            <Box  marginLeft="auto" marginRight="auto" paddingTop="20px">
                <Image src={ghostBusterBooks} height="550px" />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Building a component library is a great way to gain efficiencies in creating accessible React apps
                        - You can consolidate all of your UI widget components in one place
                            - by "UI widget" I mean our basic building block UI elements, things like:
                                - form elements such as:
                                    - text inputs
                                    - radio buttons
                                    - checkboxes
                                    - text areas
                                    - selects
                                - and then interactive elements like:
                                    - buttons
                                    - links
                                    - tabs
                                    - nav bars
                                    - etc
                                - and finally more complex items such as:
                                    - dialogs or modals
                                    - menus
                                    - number steppers
                                    - accordians
                                    - carousels
                                    - and the list goes on
                            - While of course there are still accessibility considerations once these building blocks are composed on a page,
                                - but the large bulk of potential accessibility problems lie in how these widgets are individually coded
                        - If you can write each of these widgets with accessibility in mind then you now have:
                            - a library of reusable components with accessibility baked in
                            - you don't need to solve the same problems over and over again for each app you build
                        - so the amount of issues you'll run into will be massively reduced
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library - Storybook */}
        <Slide>
            <Heading fontSize="h2">Component Library</Heading>
            <FlexBox justifyContent="center" paddingBottom="60px">
                <Box><Image src={storybookLogo} height="70px" /></Box>
                <Box paddingLeft="20px"><Heading fontSize="3em" color="primary">Storybook</Heading></Box>
            </FlexBox>
            <Box marginLeft="auto" marginRight="auto">
                <Image src="https://raw.githubusercontent.com/storybookjs/storybook/master/media/storybook-intro.gif" />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Storybook is a super useful tool for building a React component library
                        - It provides UI where you can browse the components in your library
                        - and while doing so you can:
                            - view each component in isolation
                            - and interactively test the component by being able to change the props that are passed in to it
                            and see how it behaves
                            - well before having to integrate it into an app
                        - Being able to develop components in isolation, outside the context of an app allows you to focus on:
                            - building a component that can be reusable
                            - and testing various use cases ahead of time without having to worry about application-specific dependencies
                            - so that when it's time to drop the component into your app it's already been tested on its own
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library - Install */}
         <Slide>
            <Heading fontSize="h2">Component Library</Heading>
            <FlexBox justifyContent="center" paddingBottom="60px">
                <Box><Image src={storybookLogo} height="70px" /></Box>
                <Box paddingLeft="20px"><Heading fontSize="3em" color="primary">Storybook</Heading></Box>
            </FlexBox>
            <Stepper values={[0]}>
                {(value, step) => (
                    <>
                        {step === -1 && (
                            <>
                                <Heading fontSize="2em">Install</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            npx sb init
                                        `}
                                    </CodePane>
                                </Box>
                            </>
                        )}
                        {step === 0 && (
                            <>
                                <Heading fontSize="2em">Run</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            npm run storybook
                                        `}
                                    </CodePane>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Storybook has a CLI making installation simple
                        - (STEP)
                            - Run the init command of the Storybook CLI using npx inside an existing project's root directory
                            - The command will:
                                - Install required dependencies
                                - Setup the necessary scripts to run and build Storybook
                                - Add a default configuration
                                - Add some boilerplate stories to get you started
                        - (STEP)
                            - Once that's done you simply run Storybook with 'npm run storybook'
                            - and that will start up a development server hosting your Storybook UI
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library - Testing Props */}
        <Slide>
            <Box marginLeft="auto" marginRight="auto" marginTop="-15px">
                <Image src={storybookExampleButton} height="750px" />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - As I stated before, Storybook gives you the ability to test changing the props that get passed in to
                        a component to see how its state changes based on those values
                        - You can do that on the Controls panel for the component
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library - Testing ViewPort Sizes */}
        <Slide>
            <Box marginLeft="auto" marginRight="auto">
                <video autoPlay loop height="700px">
                    <source src={storybookVideo} type="video/mp4" />
                </video>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Storybook also provides the ability to test your component using different viewport sizes
                        and against different background colors
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Component Library - Accessibility Addon */}
        <Slide>
            <Box marginLeft="auto" marginRight="auto" marginTop="-10px">
                <Image src={storybookAddon} height="750px" />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - So here's where Storybook gets even more powerful:
                            - It supports a plugin system it calls Addons.
                            - Most notably there is an Addon for auditing the accessibility of components
                            - Once installed and configured it can audit a component for accessibility issues
                            and display them in a panel
                            - The Addon uses the same rules engine that react-axe uses called axe-core
                            - Here it shows you:
                                1. Any violations
                                2. What rules were violated
                                3. A link to more information
                                4. And what WCAG and Section 508 Guidelines were violated
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Unit Testing */}
        <Slide>
            <Heading fontSize="h2">Unit Testing</Heading>
            <Box marginLeft="auto" marginRight="auto">
                <iframe src="https://giphy.com/embed/l2Je9KLRT3YkN4yas" width="730" height="616" frameBorder="0" class="giphy-embed"></iframe>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - We can also add accessibility testing to our unit tests which gives us:
                            1. an easy way to automate accessibility auditing
                            2. the flexibility to audit at any level of our code, as low as an element and as high as a component representing an entire page of our app
                            3. a safety net for any regressions
                        - For all of the reasons we write unit tests we can apply that to accessibility
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Unit Testing - install */}
        <Slide>
            <Heading fontSize="h2">Unit Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="75px">
                <Box><Image src={jestLogo} height="75px" /></Box>
                <Box><Heading fontSize="3em" color="primary">jest-axe</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Install</Heading>
            <Box marginLeft="20px" marginRight="20px">
                <CodePane
                    theme={draculaTheme}
                    language="shell"
                    autoFillHeight
                >
                    {`
                        # npm
                        npm install jest-axe --save-dev

                        # yarn
                        yarn add jest-axe --dev
                    `}
                </CodePane>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - jest-axe is a plugin to jest that also uses the axe-core rules engine
                            - that's the same rules engine we've already talked about used by react-axe and the Storybook accessibility plugin
                            - if you're noticing a theme, IMO, the axe-core rules engine is the most trusted source for accessibility auditing
                            at this time,
                            - so these tools that are built on top of it share a really thoroughm dependable and consistent foundation
                            for accessibility auditing
                        - If you're already using jest for unit testing in your project then all you'll need to do is install jest-axe
                        to your development dependencies
                        - Otherwise you'll need to also install jest
                            - So see the jest docs for more detail on getting jest configured
                        - But assuming you're already running jest, let's talk about how to add in jest-axe
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Unit Testing - writing tests */}
        <Slide>
            <Heading fontSize="h2">Unit Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="75px">
                <Box><Image src={jestLogo} height="75px" /></Box>
                <Box><Heading fontSize="3em" color="primary">jest-axe</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Writing Tests</Heading>
                <Stepper
                    values={[
                        // ReactDOM
                        [2, 2],
                        [9, 19],
                        [10, 13],
                        [15, 15],
                        [17, 17],
                        // Enzyme
                        [2, 2],
                        [10, 12],
                        [14, 14],
                        [16, 16],
                        // React Testing Library
                        [2, 2],
                        [10, 12],
                        [14, 14],
                        [16, 16],
                    ]}
                >
                    {(value, step) => (
                        <Box
                            marginLeft="20px"
                            marginRight="20px"
                            display={step === -1 ? 'none' : 'block'}
                        >
                            <Box
                                bg="black"
                                paddingLeft="10px"
                                display="block"
                            >
                                {[0, 1, 2, 3, 4].includes(step) && (
                                    <Text fontSize="1.5rem" margin="0rem">
                                        Rendering with ReactDOM
                                    </Text>
                                )}

                                {[5, 6, 7, 8].includes(step) && (
                                    <Text fontSize="1.5rem" margin="0rem">
                                        Using Enzyme
                                    </Text>
                                )}

                                {[9, 10, 11, 12].includes(step) && (
                                    <Text fontSize="1.5rem" margin="0rem">
                                        Using React Testing Library
                                    </Text>
                                )}
                            </Box>
                            {[0, 1, 2, 3, 4].includes(step) && (
                                <CodePane
                                    theme={draculaTheme}
                                    language="javascript"
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    fontSize={15}
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        import React from 'react';
                                        import ReactDOM from 'react-dom';
                                        import { axe, toHaveNoViolations } from 'jest-axe';

                                        import Login from '../Login';

                                        expect.extend(toHaveNoViolations);

                                        it('should not violate accessibility rules', async () => {
                                            const LoginPage = document.createElement('div');
                                            ReactDOM.render((
                                                <Login history={{ push: jest.fn() }} />
                                            ), LoginPage);

                                            const results = await axe(LoginPage);

                                            expect(results).toHaveNoViolations();
                                        });
                                    `)}
                                </CodePane>
                            )}
                            {[5, 6, 7, 8].includes(step) && (
                                <CodePane
                                    theme={draculaTheme}
                                    language="javascript"
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    fontSize={15}
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        import React from 'react';
                                        import { mount } from 'enzyme';
                                        import { axe, toHaveNoViolations } from 'jest-axe';

                                        import Login from '../Login';

                                        expect.extend(toHaveNoViolations);

                                        it('should not violate accessibility rules', async () => {
                                            const LoginPage = mount(
                                                <Login history={{ push: jest.fn() }} />
                                            );

                                            const results = await axe(LoginPage.getDOMNode());

                                            expect(results).toHaveNoViolations();
                                        });
                                    `)}
                                </CodePane>
                            )}
                            {[9, 10, 11, 12].includes(step) && (
                                <CodePane
                                    theme={draculaTheme}
                                    language="javascript"
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    fontSize={15}
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        import React from 'react';
                                        import { render } from '@testing-library/react';
                                        import { axe, toHaveNoViolations } from 'jest-axe';

                                        import Login from '../Login';

                                        expect.extend(toHaveNoViolations);

                                        it('should not violate accessibility rules', async () => {
                                            const { container } = render(
                                                <Login history={{ push: jest.fn() }} />
                                            );

                                            const results = await axe(container);

                                            expect(results).toHaveNoViolations();
                                        });
                                    `)}
                                </CodePane>
                            )}
                        </Box>
                    )}
                </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - you can use jest-axe with whatever rendering library you wish as long as you pass the axe function the HTML you wish to test
                        - (STEP)
                            - You can use ReactDOM to render the component you wish to test
                            - (STEP)
                                - here in this test we're rendering a component representing a Login Page
                                    - this is the same Login Page I've already showed some examples of findings from the eslint plugin and react-axe earlier
                            - (STEP)
                                - we're creating a DIV and then calling ReactDOM to render the component we want to test into that DIV
                            - (STEP)
                                - then we pass that DIV element which now contains our LoginPage component as rendered HTML to the axe function
                            - (STEP)
                                - finally we call expect on the results with the matcher that jest-axe provides us, toHaveNoViolations
                                - if a violation is found then the test will fail and the violation will be output to the console
                        - (STEP)
                            - You can also use Enzyme for rendering the component you wish to test
                            - (STEP)
                                - here we use Enzyme's mount function to render our component
                            - (STEP)
                                - then we pass the result of Enzyme's getDOMNode function to axe because remember we need to pass the axe function HTML
                            - (STEP)
                                - and then the last step is the same no matter what rendering framework we use, we just test that the result from axe has no violations
                        - (STEP)
                            - You can even use React-Testing-Library for rendering your test components
                            - (STEP)
                                - here we're using React-Testing-Library's render function to render the component
                                - and destructuring the returned object to get the container
                            - (STEP)
                                - then we can pass the container to the axe function
                            - (STEP)
                                - and once again expect that there are no violations

                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Unit Testing - running */}
        <Slide>
            <Heading fontSize="h2">Unit Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="30px">
                <Box><Image src={jestLogo} height="75px" /></Box>
                <Box><Heading fontSize="3em" color="primary">jest-axe</Heading></Box>
            </FlexBox>
            <Box marginLeft="auto" marginRight="auto">
                <Image src={jestAxeOutput} style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }} />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Here's what the output would look like for that test we just saw the examples of
                        - If a component being tested has a violation, jest will output that the test failed
                        - jest-axe adds to the output for the failed test:
                            1. the HTML element where the violation occurred
                            2. what the violation was
                            3. a list of suggestions for fixing the issue
                            4. And finally a link to more information about the rule that was violated
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing */}
        <Slide>
            <Heading fontSize="h2">End-to-End Testing</Heading>
            <Box margin="auto">
                <Image src={canTelephone} height="750px" />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - While a tool like react-axe can audit a page for accessibility at runtime you have to be there
                        manually testing your app to trigger the auditing and watching the DevTools Console for findings as you go
                        - This is great while you're developing but gets tedious as a method for repeatedly testing entire pages of your app at a time
                        - Using End-to-End testing gives us the ability to automate the auditing of our rendered pages
                        - With End-to-End testing you can program interactions with the UI to fully exercise all possible states of your page's UI widgets
                        and uncover any accessibility issues on the page
                        - Just like with unit testing, this provides a great way to build a safety net to catch any regressions as you continue to develop
                        - For End-to-End Testing we have some options - most notably we can use the axe-core rules engine with both:
                            - Selenium WebDriver
                            - and Cypress
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Webdriver - Install */}
        <Slide>
            <Heading fontSize="h2">End-to-End Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="75px">
                <Box><Image src={seleniumLogo} height="75px" /></Box>
                <Box paddingLeft="15px"><Heading fontSize="3em" color="primary">axe-webdriverjs</Heading></Box>
            </FlexBox>
            <Stepper values={[0, 1]}>
                {(value, step) => (
                    <>
                        {step === 0 && (
                            <>
                                <Heading fontSize="2em">Install Selenium WebDriver</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            # npm
                                            npm install selenium-webdriver --no-save

                                            # yarn
                                            yarn add selenium-webdriver --dev
                                        `}
                                    </CodePane>
                                </Box>
                                <Box padding="50px 20px">
                                    <Text color="secondary">For more info on installing Selenium WebDriver and browser drivers see: <Link href="https://www.selenium.dev/documentation/en/webdriver/driver_requirements/">https://www.selenium.dev/documentation/en/webdriver/driver_requirements/</Link></Text>
                                </Box>
                            </>
                        )}
                        {step === 1 && (
                            <>
                                <Heading fontSize="2em">Install axe-webdriverjs</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            # npm
                                            npm install axe-webdriverjs --save-dev

                                            # yarn
                                            yarn add axe-webdriverjs --dev
                                        `}
                                    </CodePane>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Starting with Selenium WebDriver, there is a library available
                            - axe-webdriverjs
                            - that provides WebDriver the ability to analyze the current page for accessibility issues
                        - (STEP)
                            - Start by installing Selenium WebDriver if you haven't already
                            - and see the link listed here for further details on setting up WebDriver and links to download browser drivers
                        - (STEP)
                            - Then install the library axe-webdriverjs to your development dependencies
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Webdriver - writing tests */}
        <Slide>
        <Heading fontSize="h2">End-to-End Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="75px">
                <Box><Image src={seleniumLogo} height="75px" /></Box>
                <Box paddingLeft="15px"><Heading fontSize="3em" color="primary">axe-webdriverjs</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Writing Tests</Heading>
                <Stepper
                    defaultValue={[]}
                    values={[
                        [1, 17],
                        [1, 2],
                        [4, 6],
                        [9, 9],
                        [10, 16]
                    ]}
                >
                    {(value, step) => (
                        <Box
                            marginLeft="20px"
                            marginRight="20px"
                            display={step === -1 ? 'none' : 'block'}
                        >
                            <Box
                                bg="black"
                                paddingLeft="10px"
                                display="block"
                            >
                                <Text fontFamily="monospace" fontSize="1rem" margin="0rem">
                                    e2e-tests.js
                                </Text>
                            </Box>
                            <CodePane
                                theme={draculaTheme}
                                language="javascript"
                                highlightStart={value[0]}
                                highlightEnd={value[1]}
                                fontSize={15}
                                autoFillHeight
                            >
                                {indentNormalizer(`
                                    const WebDriver = require('selenium-webdriver');
                                    const AxeBuilder = require('axe-webdriverjs');

                                    const driver = new WebDriver.Builder()
                                        .forBrowser('chrome')
                                        .build();

                                    driver
                                      .get('localhost:3000/login')
                                      .then(function() {
                                            AxeBuilder(driver).analyze(function(err, results) {
                                                if (err) {
                                                    // Handle error somehow
                                                }
                                                console.log(results);
                                            });
                                      });
                                `)}
                            </CodePane>
                        </Box>
                    )}
                </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Selenium WebDriver is run via a script.
                        - (STEP)
                            - here is a simple example test script
                        - (STEP)
                            - we only need to require:
                                - Selenium WebDriver
                                - and the axe-webdriverjs library
                        - (STEP)
                            - We build a WebDriver instance for Chrome in this example
                        - (STEP)
                            - Then we navigate to the page we want to test
                            - in this case it's the same Login Page that we've used for a number of the other tools we've looked at so far
                        - (STEP)
                            - Once the page has loaded we call the AxeBuilder we imported from the axe-webdriverjs library
                            - it requires we pass it the WebDriver instance we created initially
                            - then we can finally call the analyze function which will audit the current page against the axe-core rules set
                            and pass the results as the second argument to a provided callback function
                        - All we're doing in this callback function is logging out the results object so we can see what that looks like
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Webdriver - running */}
        <Slide>
            <Heading fontSize="h2">End-to-End Testing</Heading>
            <FlexBox justifyContent="center" paddingBottom="75px">
                <Box><Image src={seleniumLogo} height="75px" /></Box>
                <Box paddingLeft="15px"><Heading fontSize="3em" color="primary">axe-webdriverjs</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Running</Heading>
            <Box
                marginLeft="20px"
                marginRight="20px"
            >
                <CodePane
                    theme={draculaTheme}
                    language="shell"
                    autoFillHeight
                >
                    {`
                        node e2e-tests
                    `}
                </CodePane>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - To run the test script we need to run it with Node
                        - and if you noticed in the previous slide, for this example my script was named e2e-tests.js
                        - so that's the file we're running here with Node
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Webdriver - results */}
        <Slide>
            <Stepper values={[0, 1, 2, 3]}>
                {(value, step) => (
                    <FlexBox justifyContent="space-evenly" alignItems="center">
                        <Box marginLeft="auto" marginRight="auto" paddingTop="75px">
                            <Image src={webdriverChromeWindow}
                                width="500px"
                                style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                            />
                        </Box>
                        <Box paddingTop="20px">
                            { step === 0 && (
                                <Image
                                    src={webdriverResults1}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                />
                            )}
                            { step === 1 && (
                                <Image
                                    src={webdriverResults2}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                />
                            )}
                            { step === 2 && (
                                <Image
                                    src={webdriverResults3}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                />
                            )}
                            { step === 3 && (
                                <Image
                                    src={webdriverResults4}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                />
                            )}
                        </Box>
                    </FlexBox>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - once the script starts it will automatically open up a window of the browser that we specified in the test script
                        - to the url the script specified
                        - after axe-webdriverjs's analyze function is finished and returns its results to the callback,
                        - (STEP)
                            - the results object will be logged to the console as we specified in the test script
                            - the first property in the results object is the inapplicable items
                            - these are all of the rules that were found not to apply to the current page
                            - there will likely be A LOT of these
                        - (STEP)
                            - keep scrolling and eventually you'll get to the incomplete items which presumably would include any rules whose checks weren't able to be completed
                            - and then there is a passes property listing all of the rules that did apply to the page and passed
                        - (STEP)
                            - then further down you'll get to the details of the testEngine, testEnvironment, testRunner, timeStamp, toolOptions and the url being tested
                        - (STEP)
                            - and then finally is the violations property containing any rules that the page violated
                            - each violation lists:
                                - the description and detail about the rule
                                - a URL where more information is available
                                - the id of the rule
                                - how severe the violation impacts accessibility
                                - references to the Nodes and Tags that violate the rule
                                - (since these results are just simply being output using console.log we only see [Array] rather than a more useful representation of the value)
                            - one could certainly customize how the results are reported using this results object to be more tailored to their needs
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Cypress - Install */}
        <Slide>
            <Heading fontSize="h2">End-to-End Testing</Heading>
            <FlexBox justifyContent="center" alignItems="center">
                <Box><Image src={cypressLogo} height="70px" /></Box>
                <Box paddingLeft="30px" paddingBottom="15px"><Heading fontSize="3em" color="primary">cypress-axe</Heading></Box>
            </FlexBox>
            <Stepper values={[0, 1, 2, 3]}>
                {(value, step) => (
                    <>
                        {step === 0 && (
                            <Box paddingTop="75px">
                                <Heading fontSize="2em">Install Cypress</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            # npm
                                            npm install cypress --save-dev

                                            # yarn
                                            yarn add cypress --dev
                                        `}
                                    </CodePane>
                                </Box>
                            </Box>
                        )}
                        {step === 1 && (
                            <Box paddingTop="75px">
                                <Heading fontSize="2em">Install cypress-axe</Heading>
                                <Box marginLeft="20px" marginRight="20px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            # npm
                                            npm install cypress-axe --save-dev

                                            # yarn
                                            yarn add cypress-axe --dev
                                        `}
                                    </CodePane>
                                </Box>
                            </Box>
                        )}
                        {step === 2 && (
                            <>
                                <FlexBox
                                    margin="10px"
                                    flexDirection="row"
                                    justifyContent="space-evenly"
                                    alignItems="center"
                                >
                                    <Box width="475px">
                                        <Box
                                            bg="black"
                                            padding="5px"
                                            display="block"
                                        >
                                            <Text fontFamily="monospace" fontSize="1.5rem" margin="0rem">
                                                package.json
                                            </Text>
                                        </Box>

                                        <CodePane
                                            theme={draculaTheme}
                                            highlightStart={8}
                                            highlightEnd={8}
                                            fontSize={15}
                                            language="json"
                                            autoFillHeight
                                        >
                                            {indentNormalizer(`
                                                {
                                                    "scripts": {
                                                        "start": "react-scripts start",
                                                        "build": "react-scripts build",
                                                        "lint": "eslint src",
                                                        "pretest": "npm run lint",
                                                        "test": "react-scripts test",
                                                        "cypress:open": "cypress open",
                                                        "eject": "react-scripts eject",
                                                    }
                                                }
                                            `)}
                                        </CodePane>
                                    </Box>
                                    <Box>
                                        <Image
                                            src={cypressAppScreenshot}
                                            height="500px"
                                            style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                        />
                                    </Box>
                                </FlexBox>
                            </>
                        )}
                        {step === 3 && (
                            <Box>
                                <Heading fontSize="2em">Add cypress-axe Commands</Heading>
                                <Box marginLeft="auto" marginRight="auto" width="700px" paddingTop="10px">
                                    <Box
                                        bg="black"
                                        paddingLeft="10px"
                                        display="block"
                                    >
                                        <Text fontFamily="monospace" fontSize="1rem" margin="0rem">
                                            cypress/support/index.js
                                        </Text>
                                    </Box>
                                    <CodePane
                                        theme={draculaTheme}
                                        language="javascript"
                                        highlightStart={22}
                                        highlightEnd={22}
                                        fontSize="15px"
                                        autoFillHeight
                                    >
                                        {indentNormalizer(`
                                            // ***********************************************************
                                            // This example support/index.js is processed and
                                            // loaded automatically before your test files.
                                            //
                                            // This is a great place to put global configuration and
                                            // behavior that modifies Cypress.
                                            //
                                            // You can change the location of this file or turn off
                                            // automatically serving support files with the
                                            // 'supportFile' configuration option.
                                            //
                                            // You can read more here:
                                            // https://on.cypress.io/configuration
                                            // ***********************************************************

                                            // Import commands.js using ES2015 syntax:
                                            import './commands'

                                            // Alternatively you can use CommonJS syntax:
                                            // require('./commands')

                                            import 'cypress-axe';
                                        `)}
                                    </CodePane>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Continuing on to Cypress, cypress-axe is a package that adds commands to Cypress for testing the pages of your application
                        for accessibility issues using the axe-core rules engine
                        - (STEP)
                            - Start by installing Cypress if you haven't already
                        - (STEP)
                            - Then install the cypress-axe package
                        - (STEP)
                            - If this is your first time setting up Cypress, you'll want to add the command to open the Cypress app
                            to your npm scripts
                            - once you run that command, the Cypress app will automatically open in a browser window
                            - and upon running the first time will install some directory scaffolding with some example files into your project
                        - (STEP)
                            - To continue setting up cypress-axe you'll need to open up the index.js file located in the cypress/support directory
                            - add to it an import of cypress-axe
                            - this makes the additional commands provided by cypress-axe available to cypress
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Cypress - writing tests */}
        <Slide>
            <Heading fontSize="h2">End-to-End Testing</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="75px">
                <Box><Image src={cypressLogo} height="70px" /></Box>
                <Box paddingLeft="30px" paddingBottom="15px"><Heading fontSize="3em" color="primary">cypress-axe</Heading></Box>
            </FlexBox>
            <Heading fontSize="2em">Writing Tests</Heading>
                <Stepper
                    defaultValue={[]}
                    values={[
                        [2, 2],
                        [3, 4],
                        [6, 8],
                    ]}
                >
                    {(value, step) => (
                        <Box
                            marginLeft="20px"
                            marginRight="20px"
                        >
                            <CodePane
                                theme={draculaTheme}
                                language="javascript"
                                highlightStart={value[0]}
                                highlightEnd={value[1]}
                                autoFillHeight
                            >
                                {indentNormalizer(`
                                    beforeEach(() => {
                                        cy.visit('http://localhost:3000/login');
                                        cy.injectAxe();
                                    });

                                    it('should not violate accessibility rules', () => {
                                        cy.checkA11y();
                                    });
                                `)}
                            </CodePane>
                        </Box>
                    )}
                </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - So now let's look at an example test
                        - (STEP)
                            - First we call the command to visit the page we want to test
                        - (STEP)
                            - That should be followed immediately by the command to injectAxe
                            - this injects the axe-core runtime into the page so it will be available to use for auditing
                        - (STEP)
                            - Then we can proceed to check the page for accessibility issues by calling the checkA11y command
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* End-to-end Testing - Cypress - running and results */}
        <Slide>
            <Stepper values={[0, 1, 2, 3]}>
                {(value, step) => (
                    <>
                        { step === -1 && (
                            <>
                                <Heading fontSize="h2">End-to-End Testing</Heading>
                                <FlexBox justifyContent="center" alignItems="center" paddingBottom="75px">
                                    <Box><Image src={cypressLogo} height="70px" /></Box>
                                    <Box paddingLeft="30px" paddingBottom="15px"><Heading fontSize="3em" color="primary">cypress-axe</Heading></Box>
                                </FlexBox>
                                <Heading fontSize="2em">Running Tests</Heading>
                            </>
                        )}
                        { step === 0 && (
                            <Box marginLeft="auto" marginRight="auto" marginTop="50px">
                                <Image
                                    src={cypressAppRunTests}
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        { step === 1 && (
                            <Box marginLeft="auto" marginRight="auto" marginTop="20px">
                                <Image
                                    src={cypressAppRunResults}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        { step === 2 && (
                            <Box marginLeft="auto" marginRight="auto" marginTop="20px">
                                <Image
                                    src={cypressRunResultsError}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        { step === 3 && (
                            <Box marginLeft="auto" marginRight="auto" marginTop="20px">
                                <Image
                                    src={cypressRunResultsInConsole}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - To run the tests
                        - (STEP)
                            - Go to the Cypress App and find the link for the test you wish to run
                            - Here is the test we just looked at in the previous slide
                        - (STEP)
                            - Clicking on the link will open a new window in the selected browser in which to test
                            - Shown on the right will be the page under test
                            - and on the left are the test results
                            - Here you can see there were 5 accessibility violations detected
                            - and each of them is listed
                        - (STEP)
                            - by clicking on one of the accessibility errors
                            - it will highlight the elements on the page that contain the specific error
                            - additionally it will log to the Dev Console more details about the error
                        - (STEP)
                            - by opening up the DevTools Console we do indeed see more details logged about that error
                            - Because cypress-axe is using axe-core just like the axe-webdriverjs library we just talked about
                            you'll notice all of the data points listed here are the same
                        - So really this gives us the flexibility to choose which End-to-End testing framework we prefer to work with
                        without being restricted by our ability to test for accessibility.  Both of them give us the same results.
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <Box marginLeft="auto" marginRight="auto">
                <iframe src="https://giphy.com/embed/l2Je3fIeeXyYEM85G" width="750" height="634" frameBorder="0" class="giphy-embed"></iframe>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - Continuous Integration is really where a lot of these tools become the most powerful in helping us efficiently
                        achieve and maintain the accessibility of our applications.
                        - We want to employ the tools I've covered so far all along the way as we're developing
                        so that our code is accessible from the start.
                        - But continuous integration is where we can really implement that safety net to ensure that our application
                        remains accessible.
                        - With every code change we make we introduce the potential to break accessibility.
                        - The bigger our team is or the more rapidly we develop, the easier it is for a code change to slip through the net.
                        - So employing some form of accessibility auditing in our continuous integration system can go a long way in
                        helping with that.
                        - Additionally, it can be used as a gate to prevent code changes that break accessibility from being able to be merged.
                        - Let me clear, again, as I stated at the very beginning, automated auditing tools will only catch a percentage of
                        potential accessibility issues, it is no replacement for manual testing which we need to do fairly regularly.
                        - But being able to automate the auditing will still give us an immense benefit and provide an early warning system
                        that can be run much more frequently than it's practical to perform manual testing.
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - out of the box */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <Heading fontSize="3em" color="primary">Out of the Box</Heading>
            <FlexBox justifyContent="space-evenly" alightItems="center" paddingTop="50px">
                <Box>
                    <UnorderedList>
                        <Appear elementNum={0}>
                            <ListItem padding="20px">
                                <Text fontSize="1.2em">Linting with eslint-plugin-jsx-a11y</Text>
                            </ListItem>
                        </Appear>
                        <Appear elementNum={1}>
                            <ListItem padding="20px">
                                <Text fontSize="1.2em">Unit Testing with jest-axe</Text>
                            </ListItem>
                        </Appear>
                        <Appear elementNum={2}>
                            <ListItem padding="20px">
                                <Text fontSize="1.2em">End-to-End Testing with:</Text>
                                <UnorderedList>
                                    <ListItem>
                                        <Text>axe-webdriverjs</Text>
                                    </ListItem>
                                    <ListItem>
                                        <Text>cypress-axe</Text>
                                    </ListItem>
                                </UnorderedList>
                            </ListItem>
                        </Appear>
                    </UnorderedList>
                </Box>
                <Box>
                    <Image src={outOfTheBox} width="600px" />
                </Box>
            </FlexBox>
            <Notes>
                <Markdown>
                    {`
                       - Out of the box with a Continuous Integration system of your choice you have the ability to add
                       any or all of the following to your pipeline:
                            - (STEP)
                                1. linting with the eslint-plugin-jsx-a11y plugin
                            - (STEP)
                                2. running the unit tests we've written using jest-axe
                            - (STEP)
                                3. running the end-to-end tests we've written using either:
                                    - Selenium WebDriver with the axe-webdriverjs library
                                    - or Cypress with cypress-axe
                        - I'll show you quick example of how to run these tools with one of the CI systems
                            - To keep things brief I won't show the example in all of them but the concepts are the same so you should be able to
                            recreate in whatever your CI system of choice is
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - GitHub Actions - out-of-the-box CI */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="20px">
                <Box><Image src={githubLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Actions CI</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [3, 7, 1, 18],
                    // lint job
                    [10, 19, 5, 5],
                    [10, 19, 12, 17],
                    // test job
                    [21, 30, 7, 7],
                ]}
            >
                {(value, step) => (
                    <FlexBox justifyContent="space-evenly" alignItems="center">
                        <Box width="500px" height="500px">
                            <Box
                                bg="black"
                                padding="5px"
                                display="block"
                            >
                                <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                    .github/workflows/ci.yml
                                </Text>
                            </Box>

                            <CodePane
                                theme={draculaTheme}
                                highlightStart={value[0]}
                                highlightEnd={value[1]}
                                fontSize="13"
                                language="yml"
                                autoFillHeight
                            >
                                {indentNormalizer(`
                                    name: CI

                                    on:
                                        push:
                                            branches: [ master ]
                                        pull_request:
                                            branches: [ master ]

                                    jobs:
                                        lint:
                                            runs-on: ubuntu-latest
                                            name: Run eslint
                                            steps:
                                            - uses: actions/checkout@v1
                                            - uses: actions/setup-node@v1
                                                with:
                                                node-version: '14.x'
                                            - run: npm ci
                                            - run: npm run lint

                                        unit-test:
                                            runs-on: ubuntu-latest
                                            name: Run unit tests
                                            steps:
                                            - uses: actions/checkout@v1
                                            - uses: actions/setup-node@v1
                                                with:
                                                node-version: '14.x'
                                            - run: npm ci
                                            - run: npm run test-only
                                `)}
                            </CodePane>
                        </Box>
                        <Box>
                            <Box
                                bg="black"
                                padding="5px"
                                display="block"
                            >
                                <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                    package.json
                                </Text>
                            </Box>

                            <CodePane
                                theme={draculaTheme}
                                highlightStart={value[2]}
                                highlightEnd={value[3]}
                                fontSize="13"
                                language="json"
                                autoFillHeight
                            >
                                {indentNormalizer(`
                                    {
                                        "scripts": {
                                            "start": "react-scripts start",
                                            "build": "react-scripts build",
                                            "lint": "eslint src",
                                            "pretest": "npm run lint",
                                            "test-only": "react-scripts test",
                                            "test": "npm run test-only",
                                            "cypress:open": "cypress open",
                                            "eject": "react-scripts eject"
                                        },
                                        "eslintConfig": {
                                            "extends": [
                                              "react-app",
                                              "plugin:jsx-a11y/recommended"
                                            ]
                                        },
                                    }
                                `)}
                            </CodePane>
                        </Box>
                    </FlexBox>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - For this example I'll be showing a GitHub Action
                            - This is a .yml file stored at .github/workflows at the root of my project
                            - (STEP)
                                - this workflow will be trigger when any pushes or PRs are made to the master branch
                                - I've separated the workflow out into 2 jobs which will run in parallel
                            - (STEP)
                                - there's the 'lint' job which will run the 'lint' npm script I defined in my package.json file
                                - this runs eslint which will include the eslint-plugin-jsx-a11y
                            - (STEP)
                                - since I have that installed and configured in my eslint config
                            - (STEP)
                                - the second job is the 'unit-test' job
                                - this will run the 'test-only' npm script I defined in my package.json file
                                - this runs jest which will auto detect my jest test files and run the tests I've written using jest-axe
                            - The reason I have these broken up into 2 separate jobs is that I want to see any failures separately for each
                            - If these tasks were run linearly then if one fails the job will exit before executing the following job
                            - but I want to know if I have failures in both tasks rather than having to fix all failures in one and push changes before
                            I can learn about whether I have failures in the next task
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - GitHub Actions - CI results */}
        <Slide>
            <Stepper defaultValue={[]} values={[0, 1]}>
                {(value, step) => (
                    <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                        {step === -1 && (
                            <Image
                                src={githubActionResults}
                                height="650px"
                                style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                            />
                        )}
                        {step === 0 && (
                            <Image
                                src={githubActionLintResults}
                                height="650px"
                                style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                            />
                        )}
                        {step === 1 && (
                            <Image
                                src={githubActionTestResults}
                                height="650px"
                                style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                            />
                        )}
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Here's what the results look like when this workflow runs on the project we've been testing on throughout this presentation
                        - It shows that both the lint and unit test running jobs failed (as I expected them to)
                        - If we click on the failed "Run eslint" job
                        - (STEP)
                            - We see the output from the 'npm run lint' script which matches what we saw when I demonstrated using the eslint-plugin-jsx-a11y plugin earlier
                            - If we click on the failed "Run unit tests" job
                        - (STEP)
                            - We see the output from the 'npm run test-only' script which matches what we saw when I demonstrated using jest-axe earlier
                        - So we can see here how we're able to produce the same results we would get if running these tools locally but we can leverage our CI system to automate them
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Plugins */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <Box margin="50px" border="1px solid #FF0080">
                <Grid
                    gridTemplateColumns="1fr 1fr 1fr"
                    gridColumnGap="70px"
                    height="550px"
                >
                    <FlexBox
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="space-evenly"
                    >
                        <Box>
                            <Image src={githubLogo} height="75px" />
                        </Box>
                        <Box>
                            <Image src={jenkinsLogo} height="75px" />
                        </Box>
                        <Box>
                            <Image src={netlifyLogo} height="75px" />
                        </Box>
                    </FlexBox>
                    <FlexBox
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="space-evenly"
                    >
                        <Box>
                            <Image src={travisLogo} height="75px" />
                        </Box>
                        <Box>
                            <Image src={circleciLogo} height="75px" />
                        </Box>
                    </FlexBox>
                    <FlexBox
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="space-evenly"
                    >
                        <Box>
                            <Image src={LighthouseLogo} height="125px" />
                        </Box>
                        <Box>
                            <Image src={pa11yLogo} height="125px" />
                        </Box>
                    </FlexBox>
                </Grid>
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - In addition to what we can do out-of-the-box with our CI system of choice there are plugins
                        available for each of the major CI systems that we can leverage
                        - So far just about all of the tools we've talked about have been built upon the axe-core rules engine.
                        - But in the CI space there are some other players
                            - Most notably at this time are Lighthouse and pa11y
                            - Pretty much any of these CI systems can be set up to run either of these tools
                        - You've probably heard of Lighthouse before as it comes bundled with Chrome DevTools for performing a number
                        of audits on things such as performance, SEO, best practices as well as accessibility
                            - Well it's also available to use as a CLI or CI tool
                            - And once again axe shows up, as Lighthouse uses axe-core under the covers.
                        - Pa11y is also a set of CLI and CI tools that can perform accessibility audits on a provided URL or file
                            - It allows you to specify the test runner to use, and at this time comes with the options to use either:
                                - HTML CodeSniffer
                                - or axe-core
                        - I'll show you some examples for each of the top CI systems but this list is not exhaustive and there are more plugins
                        coming out all the time.
                        - So I urge you to do some research about what's out there for your system of choice.
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <Heading fontSize="3em" color="primary">Lighthouse CI</Heading>
            <Heading fontSize="2em">Configure</Heading>
            <Stepper
                defaultValue={[]}
                values={[
                    // package.json
                    [5, 6],
                    // lighthouserc.json
                    [1, 15],
                    [4, 4],
                    [5, 5],
                    [6, 8]
                ]}
            >
                {(value, step) => (
                    <>
                        { step === 0 && (
                            <Box marginRight="auto" marginLeft="auto">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        package.json
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    language="json"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        {
                                            "scripts": {
                                                "start": "react-scripts start",
                                                "build": "react-scripts build",
                                                "serve": "serve -s build",
                                                "serve:lhci": "NODE_ENV=production npm run serve",
                                                "lint": "eslint src",
                                                "pretest": "npm run lint",
                                                "test-only": "react-scripts test",
                                                "test": "npm run test-only",
                                                "cypress:open": "cypress open",
                                                "lighthouse": "lhci autorun",
                                                "eject": "react-scripts eject"
                                            },
                                        }
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                        { [1, 2, 3, 4].includes(step) && (
                            <Box marginRight="auto" marginLeft="auto" width="600px">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        lighthouserc.json
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    language="json"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        {
                                            "ci": {
                                                "collect": {
                                                    "url": ["http://localhost:5000/login"],
                                                    "isSinglePageApplication": true,
                                                    "settings": {
                                                        "onlyCategories": ["accessibility"]
                                                    }
                                                }
                                            }
                                        }
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - (STEP)
                            - The first thing needed to set up the Lighthouse CI is in the package.json for your project:
                                - add a script to serve the production build
                                    - I chose to use the serve package but you can use whatever you want just make sure to npm or yarn install it as well
                                - and then add a script named "serve:lhci"
                                    - this is used by Lighthouse CI to detect what is should run to start your server
                                    - there are also options for letting Lighthouse CI serve static files in its own server so you can do what
                                    best suits your project
                        - (STEP)
                            - Next you'll need to create a file in the root of your project named lighthouserc.json.
                            - There are extensive options for configuring Lighthouse CI but this was just the minimum needed to get up and running.
                            - I encourage you to check their docs for more options.
                        - (STEP)
                            - the 'url' property takes an array of URLs I want Lighthouse to audit
                                - here I've just specified my Login Page which we've been testing throughout the presentation
                                - note it's at localhost and the port is specifically 5000 because that's what 'serve' is going to
                                host it at
                                - make sure the port is set to whatever your server will be using
                        - (STEP)
                            - the next property 'isSinglePageApplication" needs to be set to true for Single Page Applications
                        - (STEP)
                            - The settings property here is specifying that I only want Lighthouse to audit for accessibility issues
                            - Because Lighthouse performs many categories of audits I added this to restrict the results just to
                            accessibility issues
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI - GitHub Action */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="10px">
                <Box><Image src={githubLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Lighthouse CI Action</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [3, 7],
                    [14, 14],
                    [15, 15],
                    [16, 16],
                    [17, 17],
                    [18, 18]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="800px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .github/workflows/lighthouse.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                name: Lighthouse

                                on:
                                    push:
                                        branches: [ master ]
                                    pull_request:
                                        branches: [ master ]

                                jobs:
                                    lighthouse:
                                        runs-on: ubuntu-latest
                                        name: Audit with Lighthouse
                                        steps:
                                            - uses: actions/checkout@v2
                                            - uses: actions/setup-node@v1
                                            - run: npm install && npm install -g @lhci/cli@0.4.x
                                            - run: npm run build
                                            - run: lhci autorun --upload.target=temporary-public-storage
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - So if you want to use Lighthouse with GitHub Actions, similarly to what I already demonstrated,
                        - you'll need to create a .yml file in the .github/workflow directory at the root of your project
                        - (STEP)
                            - again this workflow is triggered on push and PRs of the master branch
                        - (STEP)
                            - the workflow will do a checkout of the repo
                        - (STEP)
                            - setup node
                        - (STEP)
                            - and then install the Lighthouse Continuous Integration CLI
                        - (STEP)
                            - it will then build the project
                        - (STEP)
                            - and finally runs Lighthouse
                            - using the configuration we defined in the 'lighthouserc.json' file in our project
                            - we could also pass flags into the 'lhci autorun' command in place of using the json config file
                            - here you can see I'm passing the upload.target flag
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI - Travis CI */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="75px">
                <Box><Image src={travisLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Lighthouse CI</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [1, 10],
                    [4, 5],
                    [7, 7],
                    [8, 8]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="800px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .travis.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                language: node_js
                                node_js:
                                  - 14
                                before_install:
                                  - npm install -g @lhci/cli@0.3.x
                                script:
                                  - npm run build
                                  - lhci autorun --upload.target=temporary-public-storage
                                addons:
                                  chrome: stable
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - The setup for running Lighthouse with Travis is pretty similar to the GitHub Action
                        - Create a yml file named .travis.yml at the root of your project
                        - (STEP)
                            - There's a "before_install" step to install the Lighthouse Continuous Integration CLI
                        - (STEP)
                            - And then just like the GitHub Action we'll run the build of our app
                        - (STEP)
                            - And then run Lighthouse
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI - Circle CI */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="75px">
                <Box><Image src={circleciLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Lighthouse CI</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [1, 12],
                    [8, 8],
                    [9, 9],
                    [10, 10],
                    [11, 11],
                    [12, 12]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="800px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .circleci/config.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                version: 2.1
                                jobs:
                                  build:
                                    docker:
                                      - image: circleci/node:10.16-browsers
                                    working_directory: ~/your-project
                                    steps:
                                      - checkout
                                      - run: npm install
                                      - run: npm run build
                                      - run: sudo npm install -g @lhci/cli@0.4.x
                                      - run: lhci autorun --upload.target=temporary-public-storage
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - For CircleCI, again the setup is not terribly different
                        - Place a yml file named config.yml in a .circleci directory at the root of your project
                        - And the necessary steps are exactly the same
                        - (STEP)
                            - checkout the repo
                        - (STEP)
                            - install npm dependencies
                        - (STEP)
                            - build the app
                        - (STEP)
                            - Install Lighthouse
                        - (STEP)
                            - And run Lighthouse
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI - GitHub Status Checks */}
        <Slide>
            <Stepper defaultValue={[]} values={[0, 1]}>
                {(value, step) => (
                    <>
                        {step === -1 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                                <Image
                                    src={lighthouseAppInstall}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        {step === 0 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="75px">
                                <Image
                                    src={lighthouseAppStatusCheck}
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        {step === 1 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                                <Image
                                    src={lighthouseReport}
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - If you're using GitHub and integrate your CI system with your repos there, there's a really cool way
                        to see the Lighthouse results in much nicer, more readable format than what you'd see in the CI log
                        - There is an official Lighthouse CI GitHub App that you'll need to install
                        - (STEP)
                            - once you've got it installed and wired up with your CI system, you should start to see Status Checks for Lighthouse on your PRs and/or commits,
                            whichever you have your CI tool triggered by
                            - A Status Check will show up for each URL you specified for Lighthouse to audit as well as the overall rating for that URL
                            - Since I only specified for Lighthouse to audit my login page we only have the one status check showing here
                            - Then by clicking the Details link on the Status Check
                        - (STEP)
                            - It will open the Lighthouse Report for that URL
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Jenkins - Lighthouse CI */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center">
                <Box><Image src={jenkinsLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Lighthouse CI</Heading></Box>
            </FlexBox>
            <Stepper defaultValue={[]} values={[0, 1, 2]}>
                {(value, step) => (
                    <>
                        {step === 0 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="10px">
                                <Image
                                    src={jenkinsPlugin}
                                    height="500px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        {step === 1 && (
                            <Box paddingTop="75px" marginLeft="20px" marginRight="20px">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        machine-setup.sh
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    language="shell"
                                    fontSize="15"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        #!/bin/bash

                                        set -euxo pipefail

                                        # Add Chrome's apt-key
                                        echo "deb [arch=amd64] http://dl.google.com/linux/chrome/deb/ stable main" | sudo tee -a /etc/apt/sources.list.d/google.list
                                        wget -q -O - https://dl.google.com/linux/linux_signing_key.pub | sudo apt-key add -

                                        # Add Node's apt-key
                                        curl -sL https://deb.nodesource.com/setup_10.x | sudo -E bash -

                                        # Install NodeJS and Google Chrome
                                        sudo apt-get update
                                        sudo apt-get install -y nodejs google-chrome-stable
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                        {step === 2 && (
                            <Box paddingTop="75px" marginLeft="20px" marginRight="20px">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        job.sh
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    language="shell"
                                    fontSize="15"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        #!/bin/bash

                                        set -euxo pipefail

                                        npm install
                                        npm run build

                                        export CHROME_PATH=$(which google-chrome-stable)
                                        export LHCI_BUILD_CONTEXT__EXTERNAL_BUILD_URL="$BUILD_URL"

                                        npm install -g @lhci/cli@0.4.x
                                        lhci autorun
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - If you're using Jenkins as your CI Tool of choice don't feel left out as it also has the ability to use Lighthouse
                        - (STEP)
                            - there is a Plugin available through the Plugins Manager in Jenkins
                            - Or you can write your own build script to use the Lighthouse CI tool similarly to how the other CI tools I've
                            demonstrated so far
                        - (STEP)
                            - Some setup may have to be done if you haven't already configured your build machine to support Node and Chrome
                        - (STEP)
                            - And then your build script will run those say key steps that the others have:
                                - install your npm packages
                                - build your app
                                - install the Lighthouse Continuous Integration CLI
                                - and then run it
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Lighthouse CI Server */}
        <Slide>
            <Stepper defaultValue={[]} values={[0, 1]}>
                {(value, step) => (
                    <>
                        {step === -1 && (
                            <>
                                <Heading fontSize="h2">Continuous Integration</Heading>
                                <Heading fontSize="3em" color="primary">The Lighthouse CI Server</Heading>
                            </>
                        )}
                        {step === 0 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                                <Image
                                    src="https://user-images.githubusercontent.com/2301202/79480502-c8af9a80-7fd3-11ea-8087-52f6c8ba6f03.png"
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                        {step === 1 && (
                            <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                                <Image
                                    src="https://user-images.githubusercontent.com/39191/68522269-7917b680-025e-11ea-8d96-2774c0a0b04c.png"
                                    height="650px"
                                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .9), -8px 0px 1.5em rgba(0, 1, 19, .9)' }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - I've only really scratched the surface here on using Lighthouse with your CI system.  If you dig into the docs there are a ton of options
                        for further configuration and there are some really powerful reporting options.
                        - You can even set up your own Lighthouse CI Server.
                        - (STEP)
                            - The Lighthouse CI Server is an open source node server that you can host in a cloud provider or on your local machine,
                            - essentially as a personal storage and visualization center for the history of your Lighthouse reports for your project.
                            - Where the methods I've shown you so far will just show you the report that was just generated in a temporary storage location,
                            they don't provide the ability to persist or compare a history
                            - The Lighthouse CI Server though gives you the ability to store all of the reports and view data points about that history
                            - You can see how your score has varied over a timeline
                        - (STEP)
                            - You can generate graphs showing improvements and regressions of scores or metric performance.
                            - And you can view diffs between any two Lighthouse reports to find the root cause of a regression.
                        - There's a lot of exciting and powerful stuff here to explore!
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - Configure */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <Heading fontSize="3em" color="primary">Pa11y CI</Heading>
            <Heading fontSize="2em">Configure</Heading>
            <Stepper
                defaultValue={[]}
                values={[
                    // install
                    0,
                    // package.json
                    [14, 14],
                    // .pa11y.ci
                    [1, 8],
                    [2, 4],
                    [5, 7]
                ]}
            >
                {(value, step) => (
                    <>
                        { step === 0 && (
                            <Box marginRight="auto" marginLeft="auto" paddingTop="75px" width="650px">
                                <CodePane
                                    theme={draculaTheme}
                                    language="shell"
                                    autoFillHeight
                                >
                                    {`
                                        # npm
                                        npm install pa11y-ci --save-dev

                                        # yarn
                                        yarn add pa11y-ci --dev
                                    `}
                                </CodePane>
                            </Box>
                        )}
                        { step === 1 && (
                            <Box marginRight="auto" marginLeft="auto" paddingTop="50px">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        package.json
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    fontSize="15px"
                                    language="json"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        {
                                            "scripts": {
                                                "start": "react-scripts start",
                                                "build": "react-scripts build",
                                                "serve": "serve -s build",
                                                "serve:prod": "NODE_ENV=production npm run serve",
                                                "serve:lhci": "npm run serve:prod",
                                                "lint": "eslint src",
                                                "pretest": "npm run lint",
                                                "test-only": "react-scripts test",
                                                "test": "npm run test-only",
                                                "cypress:open": "cypress open",
                                                "lighthouse": "lhci autorun",
                                                "pa11y": "pa11y-ci",
                                                "eject": "react-scripts eject"
                                            },
                                        }
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                        { [2, 3, 4].includes(step) && (
                            <Box marginRight="auto" marginLeft="auto" paddingTop="75px" width="600px">
                                <Box
                                    bg="black"
                                    padding="5px"
                                    display="block"
                                >
                                    <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                        .pa11yci
                                    </Text>
                                </Box>

                                <CodePane
                                    theme={draculaTheme}
                                    highlightStart={value[0]}
                                    highlightEnd={value[1]}
                                    language="json"
                                    autoFillHeight
                                >
                                    {indentNormalizer(`
                                        {
                                            "urls": [
                                                "http://localhost:5000/login"
                                            ],
                                            "runners": [
                                                "axe"
                                            ]
                                        }
                                    `)}
                                </CodePane>
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Now let's talk about the Pa11y CI tool
                        - Some benefits of using Pa11y over Lighthouse are that it's:
                            - easier to get set up and running faster
                            - the output results are easier to read
                            - and it offers the flexibility to specify the test runner
                                - while it uses HTML CodeSniffer by default you can also configure it
                                to use aXe or both
                        - Let's go over the steps to getting it set up
                        - (STEP)
                            - First you'll need to install the pa11y-ci package in your project
                        - (STEP)
                            - Then you'll need to add an npm script for running pa11y-ci that
                            you'll use from your CI tool
                        - (STEP)
                            - Then you'll need to create a .pa11yci file at the root of your project
                            - Here's a pretty basic example though there are a lot of things you can
                            configure if desired
                        - (STEP)
                            - It takes an array of URLs to test
                            - Just like in the Lighthouse examples I'm just specifying my Login Page to start
                        - (STEP)
                            - And then here in the runners array you can specify whether you want to use HTML CodeSniffer,
                            aXe-core, or both
                            - Here I've chosen to just use aXe
                        - Now let's set this up with some of the CI systems
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - GitHub Action */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="10px">
                <Box><Image src={githubLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">Pa11y CI Action</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [18, 18],
                    [19, 19]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="800px" paddingTop="30px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .github/workflows/pa11y.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            fontSize="13"
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                name: Pa11y

                                on:
                                  push:
                                    branches: [ master ]
                                  pull_request:
                                    branches: [ master ]

                                jobs:
                                  pa11y:
                                    runs-on: ubuntu-latest
                                    name: Audit with Lighthouse
                                    steps:
                                      - uses: actions/checkout@v2
                                      - uses: actions/setup-node@v1
                                      - run: npm ci
                                      - run: npm run build
                                      - run: npm run serve:prod & sleep 5
                                      - run: npm run pa11y
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - So if you want to use Pa11y CI with GitHub Actions
                        - you'll need to create a .yml file in the .github/workflow directory at the root of your project
                        - The file contents will be very similar to the examples we've already seen
                        - The main differences are that we'll
                        - (STEP)
                            - specifically running a command to start a server serving up our built application
                            - and wait 5 seconds to give it time to start up and accept connections
                        - (STEP)
                            - and then we're running that command I added to my package.json scripts for running pa11y-ci
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - Travis CI  */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="75px">
                <Box><Image src={travisLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">pa11y-ci</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [5, 5],
                    [6, 6]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="800px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .travis.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                language: node_js
                                node_js:
                                  - 14
                                script:
                                  - npm run build
                                  - npm run serve:prod & sleep 5; npm run pa11y;
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - Here's an example of the yml file needed to run pa11y with Travis CI
                        - It's pretty simple and the key steps are the same
                        - (STEP)
                            - again we're just building our project
                        - (STEP)
                            - and then serving our app and running pa11y-ci
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - Circle CI  */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center" paddingBottom="10px">
                <Box><Image src={circleciLogo} height="75px" /></Box>
                <Box paddingLeft="30px" paddingBottom="15px"><Heading fontSize="3em" color="primary">pa11yci Orb</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    [4, 12],
                    [14, 15],
                    [21, 23]
                ]}
            >
                {(value, step) => (
                    <Box marginRight="auto" marginLeft="auto" width="600px">
                        <Box
                            bg="black"
                            padding="5px"
                            display="block"
                        >
                            <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                .circleci/config.yml
                            </Text>
                        </Box>

                        <CodePane
                            theme={draculaTheme}
                            highlightStart={value[0]}
                            highlightEnd={value[1]}
                            fontSize="13"
                            language="yml"
                            autoFillHeight
                        >
                            {indentNormalizer(`
                                version: 2.1

                                jobs:
                                  build:
                                    docker:
                                      - image: circleci/node:latest
                                    working_directory: ~/your-project
                                    steps:
                                      - checkout
                                      - run: npm install
                                      - run: npm run build
                                      - run: npm run serve:prod

                                orbs:
                                  pa11yci: nebulab/pa11yci

                                workflows:
                                  a11y-audit:
                                    jobs:
                                      - build
                                      - pa11yci/a11y-audit:
                                        requires:
                                          - build
                            `)}
                        </CodePane>
                    </Box>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - To use pa11y with Circle CI there is a pa11yci Orb available
                        - (STEP)
                            - So we've got a build job that does the build and serving of our app
                        - (STEP)
                            -And we've got the pa11yci orb registered
                        - (STEP)
                            - and finally we define the workflow where the pa11yci job will run after the build job
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - results  */}
        <Slide>
            <Box marginLeft="auto" marginRight="auto" paddingTop="50px">
                <Image
                    src={pa11yTravisResults}
                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - And here is what the results of running pa11y-ci look like in our CI log
                        - It lists 3 errors found
                        - details about what was wrong with each
                        - and the specific element found with the error
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - Netlify */}
        <Slide>
            <Heading fontSize="h2">Continuous Integration</Heading>
            <FlexBox justifyContent="center" alignItems="center">
                <Box><Image src={netlifyLogo} height="75px" /></Box>
                <Box paddingLeft="30px"><Heading fontSize="3em" color="primary">netlify-plugin-a11y</Heading></Box>
            </FlexBox>
            <Stepper
                defaultValue={[]}
                values={[
                    // install
                    0,
                    // netlify.toml
                    [1, 23],
                    [1, 2],
                    [8, 12],
                    [15, 15],
                    [17, 17]
                ]}
            >
                {(value, step) => (
                    <>
                        { step === 0 && (
                            <Box paddingTop="75px">
                                <Heading fontSize="2em">Install</Heading>
                                <Box marginRight="auto" marginLeft="auto" width="650px">
                                    <CodePane
                                        theme={draculaTheme}
                                        language="shell"
                                        autoFillHeight
                                    >
                                        {`
                                            # npm
                                            npm install netlify-plugin-a11y

                                            # yarn
                                            yarn add netlify-plugin-a11y
                                        `}
                                    </CodePane>
                                </Box>
                            </Box>
                        )}
                        { [1, 2, 3, 4, 5].includes(step) && (
                            <Box paddingTop="50px">
                                <Heading fontSize="2em">Configure</Heading>
                                <Box marginRight="auto" marginLeft="auto" width="1100px">
                                    <Box
                                        bg="black"
                                        padding="5px"
                                        display="block"
                                    >
                                        <Text fontFamily="monospace" fontSize="1.2rem" margin="0rem">
                                            netlify.toml
                                        </Text>
                                    </Box>

                                    <CodePane
                                        theme={draculaTheme}
                                        highlightStart={value[0]}
                                        highlightEnd={value[1]}
                                        fontSize="15px"
                                        language="toml"
                                        autoFillHeight
                                    >
                                        {indentNormalizer(`
                                            [[plugins]]
                                            package = "netlify-plugin-a11y"

                                            # all inputs are optional, we just show you the defaults below
                                            [plugins.inputs]

                                            # you can give an array of directories or paths to html files, that you want to run a11y checks on
                                            checkPaths = [
                                                '/blog',
                                                '/about.html',
                                                '/super/specific/route/index.html',
                                            ]

                                            # # optional config
                                            # ignoreDirectories = ['/admin']  # explicitly ignore these directories

                                            # resultMode = "warn" # is "error" by default
                                        `)}
                                    </CodePane>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Stepper>
            <Notes>
                <Markdown>
                    {`
                        - So just like Jenkins was a bit of a different animal when it came to using Lighthouse, we have
                        Netlify Build which deviates a little from how the other CI tools run Pa11y
                        - It's got it's own plugin available from Netlify
                        - To set it up
                        - (STEP)
                            - first install the npm package for the plugin
                        - (STEP)
                            - next you'll need to add a netlify.toml file to the root of your project
                            - here is an example file configured to use the plugin
                        - (STEP)
                            - Under plugins is specified the a11y plugin
                        - (STEP)
                            - The checkPaths is where you specify any file names or directories that should be tested with pa11y
                        - (STEP)
                            - You can optionally specify any directories that should be ignore by pa11y
                        - (STEP)
                            - And finally you can set the results of the audit to produce warnings rather than errors
                            which will fail the build
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Continuous Integration - Pa11y CI - Netlify - results  */}
        <Slide>
            <Box marginLeft="auto" marginRight="auto" paddingTop="20px">
                <Image
                    src="https://user-images.githubusercontent.com/6764957/77147207-226b8600-6a63-11ea-91b2-2de449ef6682.png"
                    height="650px"
                    style={{ boxShadow: '8px 8px 1.5em rgba(0, 1, 19, .5), -8px 0px 1.5em rgba(0, 1, 19, .5)' }}
                />
            </Box>
            <Notes>
                <Markdown>
                    {`
                        - And here is an example of what the results look like in the build log when the mode is set to error
                        - You can see that the Build is indeed failed
                        - it points out that there were 4 accessibility issues found
                        - It lists a description of each error and the element that contains the error
                    `}
                </Markdown>
            </Notes>
        </Slide>
        {/* Resources */}
        <Slide>
            <Heading fontSize="h2">Resources</Heading>
            <Box paddingBottom="100px" marginTop="-15px">
                <Text fontSize="h3" textAlign="center"><Link href="https://erindoyle.dev/talks">https://erindoyle.dev/talks</Link></Text>
            </Box>
            <Grid
                gridTemplateColumns="1fr 1fr 1fr"
                height="150px"
            >
                <Box paddingLeft="100px" marginLeft="auto" marginRight="auto">
                    <Heading fontSize="2em">Linting</Heading>
                    <UnorderedList>
                        <ListItem>
                            <Text color="secondary" fontSize="1em"><Link href="https://github.com/jsx-eslint/eslint-plugin-jsx-a11y">eslint-plugin-jsx-a11y</Link></Text>
                        </ListItem>
                    </UnorderedList>
                </Box>
                <Box padding="0 100px" marginLeft="auto" marginRight="auto">
                    <Heading fontSize="2em">Runtime</Heading>
                    <UnorderedList>
                        <ListItem>
                            <Text color="secondary" fontSize="1em"><Link href="https://github.com/dequelabs/react-axe">react-axe</Link></Text>
                        </ListItem>
                    </UnorderedList>
                </Box>
                <Box paddingRight="100px" marginLeft="auto" marginRight="auto">
                    <Heading fontSize="2em">Unit Testing</Heading>
                    <UnorderedList>
                        <ListItem>
                            <Text color="secondary" fontSize="1em"><Link href="https://github.com/nickcolley/jest-axe">jest-axe</Link></Text>
                        </ListItem>
                    </UnorderedList>
                </Box>
            </Grid>
            <Grid
                gridTemplateColumns="1fr 1fr"
                height="300px"
            >
                <Box marginLeft="auto" marginRight="auto" paddingLeft="750px5px">
                    <Heading fontSize="2em">End-to-End Testing</Heading>
                    <UnorderedList>
                        <ListItem>
                            <Text color="secondary" fontSize="1em"><Link href="https://github.com/dequelabs/axe-webdriverjs">axe-webdriverjs</Link></Text>
                        </ListItem>
                        <ListItem>
                            <Text color="secondary" fontSize="1em"><Link href="https://github.com/avanslaars/cypress-axe">cypress-axe</Link></Text>
                        </ListItem>
                    </UnorderedList>
                </Box>
                <Box marginLeft="auto" marginRight="auto" paddingRight="100px">
                    <Heading fontSize="2em">Continuous Integration</Heading>
                    <FlexBox flexDirection="row" justifyContents="space-evenly" alignItems="start">
                        <Box padding="20px">
                            <Heading fontSize="1.5em">Lighthouse CI</Heading>
                            <UnorderedList>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/GoogleChrome/lighthouse-ci">Lighthouse CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/GoogleChrome/lighthouse-ci/blob/a5220a06fc07b4d8c6245abf805424d152016994/docs/getting-started.md#configure-your-ci-provider">GitHub Action</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/hchiam/learning-lighthouse-ci">Travis CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/GoogleChrome/lighthouse-ci/blob/a5220a06fc07b4d8c6245abf805424d152016994/docs/getting-started.md#configure-your-ci-provider">Circle CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://plugins.jenkins.io/lighthouse-report/">Jenkins</Link></Text>
                                </ListItem>
                            </UnorderedList>
                        </Box>
                        <Box padding="20px">
                            <Heading fontSize="1.5em">Pa11y CI</Heading>
                            <UnorderedList>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/pa11y/pa11y-ci">Pa11y CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://cruft.io/posts/automated-accessibility-testing-node-travis-ci-pa11y/">Travis CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/nebulab/circleci-orbs-pa11yci">Circle CI</Link></Text>
                                </ListItem>
                                <ListItem>
                                    <Text color="secondary" fontSize="1em"><Link href="https://github.com/netlify-labs/netlify-plugin-a11y">Netlify</Link></Text>
                                </ListItem>
                            </UnorderedList>
                        </Box>
                    </FlexBox>
                </Box>
            </Grid>
            <Notes>
                <Markdown>
                    {`
                        - And that wraps up all of the awesome tools I wanted to share with you today!
                        - I'll end here with a list of resource links to all of the tools I talked about so you
                        can go check them out and add them to your Dev workflow!
                        - You can get to these links where I've posted the slides at:
                            - erindoyle.dev/talks and clicking on the link to React Global Summit 2020
                    `}
                </Markdown>
            </Notes>
        </Slide>
    </Deck>
);

ReactDOM.render(<Presentation />, document.getElementById('root'));
